import React from 'react'
import intl from 'react-intl-universal'
import { LOCALE } from '@foxrenderfarm/internals'
import {
  LightDropDownColumns,
  LightDropDownDivider,
  LightDropDownRow,
  LightDropDownSection,
  LightDropDownTitle,
  LightNavDropDownWrapper,
  LightSupportFooter,
  SupportDropdownSection,
} from '../../atoms'
import {
  ArnoldSolutionIcon,
  BlenderSolutionIcon,
  C4DSolutionIcon,
  DeveloperCenterIcon,
  GeneralGuideIcon,
  HoudiniSolutionIcon,
  MayaSolutionIcon,
  MaxSolutionIcon,
  NukeSolutionIcon,
  PricingBillingIcon,
  SupportCenterIcon,
  SupportContactIcon,
  UnrealSolutionIcon,
  UserCenterIcon,
  VRaySolutionIcon,
} from '../../svg'
import { showContact } from '../../../../utils'
import { LightNavDropDownPanel, Trigger, getLocalAppOrigin, getSupportSectionTitle } from './common'

interface SupportLightDropDownProps {
  currentLocale: LOCALE
  currentPathname: string
  origin: string
}

export const SupportLightDropDown = ({
  currentLocale,
  currentPathname,
  origin,
}: SupportLightDropDownProps) => {
  const supportOrigin = getLocalAppOrigin(origin, 8087)
  const support = (path: string) => `${supportOrigin}${path}`
  const tos = [
    '/support/',
    '/support/pricing-and-billing/',
    '/support/rendering-tutorial/',
    '/support/user-center/',
    '/support/general/',
    '/support/developer-center/',
  ]
  const active = tos.some((p) => currentPathname.includes(p.replace(/^\//, '')))

  const link = (
    path: string,
    label: string,
    Icon?: React.ComponentType<{ style?: React.CSSProperties }>,
  ) => (
    <LightDropDownRow as='a' href={support(path)}>
      {Icon && <Icon style={{ marginRight: 8 }} />}
      {label}
    </LightDropDownRow>
  )

  const tutorialLinks: {
    to?: string
    label: string
    Icon?: React.ComponentType<{ style?: React.CSSProperties }>
  }[] = [
    { to: '/support/rendering-tutorial/blender/', label: 'Blender', Icon: BlenderSolutionIcon },
    { to: '/support/rendering-tutorial/cinema-4-d/', label: 'Cinema 4D', Icon: C4DSolutionIcon },
    {
      to: '/support/rendering-tutorial/arnold-standalone/',
      label: 'Arnold',
      Icon: ArnoldSolutionIcon,
    },
    { to: '/support/rendering-tutorial/3ds-max/', label: '3ds Max', Icon: MaxSolutionIcon },
    {
      to: '/support/rendering-tutorial/fox-support-dir-4/',
      label: 'Houdini',
      Icon: HoudiniSolutionIcon,
    },
    { to: '/support/rendering-tutorial/vray/', label: 'V-Ray', Icon: VRaySolutionIcon },
    { to: '/support/rendering-tutorial/maya/', label: 'Maya', Icon: MayaSolutionIcon },
    {
      to: '/support/rendering-tutorial/unreal-engine/',
      label: 'Unreal Engine',
      Icon: UnrealSolutionIcon,
    },
    { to: '/support/rendering-tutorial/fox-support-dir-3/', label: 'Nuke', Icon: NukeSolutionIcon },
  ]

  return (
    <LightNavDropDownWrapper $isActive={active} $panelMinWidth={900} $panelLeft='-120%'>
      <Trigger>{intl.get('index.header.support')}</Trigger>
      <LightNavDropDownPanel style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
        <LightDropDownColumns style={{ alignItems: 'flex-start', gap: 41 }}>
          <div style={{ minWidth: 218 }}>
            <LightDropDownTitle style={{ marginBottom: 20 }}>
              {getSupportSectionTitle(currentLocale, 'guides')}
            </LightDropDownTitle>
            <LightDropDownSection>
              {link(
                '/support/general/',
                intl.get('index.header.support.general'),
                GeneralGuideIcon,
              )}
              {link(
                '/support/developer-center/',
                intl.get('index.header.support.developerCenter'),
                DeveloperCenterIcon,
              )}
            </LightDropDownSection>
          </div>
          <LightDropDownDivider />
          <div style={{ minWidth: 230 }}>
            <LightDropDownTitle style={{ marginBottom: 24 }}>
              {getSupportSectionTitle(currentLocale, 'account')}
            </LightDropDownTitle>
            <LightDropDownSection>
              {link('/support/user-center/', intl.get('index.header.support.user'), UserCenterIcon)}
              {link(
                '/support/pricing-and-billing/',
                intl.get('index.header.support.pricing.billing'),
                PricingBillingIcon,
              )}
              <LightDropDownRow as='button' type='button' onClick={showContact}>
                <SupportContactIcon style={{ marginRight: 8 }} />
                {intl.get('index.header.support.contact247')}
              </LightDropDownRow>
            </LightDropDownSection>
          </div>
          <LightDropDownDivider />
          <div style={{ minWidth: 528 }}>
            <LightDropDownTitle style={{ marginBottom: 24 }}>
              {getSupportSectionTitle(currentLocale, 'renderingTutorial')}
            </LightDropDownTitle>
            <SupportDropdownSection>
              {[0, 1, 2].map((colIndex) => (
                <div key={colIndex} style={{ display: 'flex', flexDirection: 'column' }}>
                  {tutorialLinks.slice(colIndex * 3, colIndex * 3 + 3).map((item) =>
                    item.to ? (
                      link(item.to, item.label, item.Icon)
                    ) : (
                      <LightDropDownRow key={item.label} as='span' style={{ cursor: 'default' }}>
                        {item.Icon && <item.Icon style={{ marginRight: 8 }} />}
                        {item.label}
                      </LightDropDownRow>
                    ),
                  )}
                </div>
              ))}
            </SupportDropdownSection>
          </div>
        </LightDropDownColumns>
        <LightSupportFooter href={support('/support/')}>
          {intl.get('index.header.support.center')}
          <SupportCenterIcon style={{ marginLeft: 8 }} />
        </LightSupportFooter>
      </LightNavDropDownPanel>
    </LightNavDropDownWrapper>
  )
}
